import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  ClipboardCheck,
  PackageCheck,
  Truck,
  PackageOpen,
  ClipboardList,
  AlertTriangle,
} from "lucide-react";

import { ordersService } from "../../../services/orders";
import { damagesService } from "../../../services/damages";
import { Skeleton } from "../../../components/feedback/Skeleton";
import { formatDateTime } from "../../../utils/dates";
import { CardShell } from "./CardShell";

// Status buckets → dashboard widgets. Order matches the phase brief.
const BUCKETS = [
  {
    key: "pending",
    labelKey: "warehouse_dashboard.pending",
    bodyKey: "warehouse_dashboard.pending_body",
    icon: ClipboardCheck,
    statuses: ["order_placed"],
  },
  {
    key: "in_progress",
    labelKey: "warehouse_dashboard.in_progress",
    bodyKey: "warehouse_dashboard.in_progress_body",
    icon: PackageCheck,
    statuses: ["confirmed", "in_progress"],
  },
  {
    key: "shipped",
    labelKey: "warehouse_dashboard.shipped",
    bodyKey: "warehouse_dashboard.shipped_body",
    icon: Truck,
    statuses: ["shipped"],
  },
  {
    key: "awaiting_collection",
    labelKey: "warehouse_dashboard.awaiting_collection",
    bodyKey: "warehouse_dashboard.awaiting_collection_body",
    icon: PackageOpen,
    statuses: ["delivered", "collected"],
  },
  {
    key: "quality_testing",
    labelKey: "warehouse_dashboard.quality_testing",
    bodyKey: "warehouse_dashboard.quality_testing_body",
    icon: ClipboardList,
    statuses: ["quality_testing"],
  },
];

function CountCard({ bucket, count, loading, onClick }) {
  const { t } = useTranslation();
  const Icon = bucket.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left rounded-lg bg-surface p-5 shadow-card transition-colors hover:bg-offwhite focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-champagne min-h-[160px] flex flex-col"
      data-testid={`warehouse-widget-${bucket.key}`}
    >
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-text-primary">
          {t(bucket.labelKey)}
        </h2>
        <span
          className="flex h-8 w-8 items-center justify-center rounded-full bg-offwhite"
          aria-hidden
        >
          <Icon className="h-4 w-4 text-champagne/70" />
        </span>
      </div>
      {loading ? (
        <Skeleton className="h-10 w-16" />
      ) : (
        <span
          className="text-5xl font-semibold tracking-tight text-text-primary"
          data-testid={`warehouse-widget-${bucket.key}-count`}
        >
          {count}
        </span>
      )}
      <p className="mt-2 text-xs text-text-secondary">{t(bucket.bodyKey)}</p>
    </button>
  );
}

function RecentDamagesWidget({ damages, loading, onNavigate }) {
  const { t, i18n } = useTranslation();
  return (
    <CardShell
      title={t("warehouse_dashboard.recent_damages")}
      icon={AlertTriangle}
      testId="warehouse-widget-recent-damages"
    >
      {loading ? (
        <div className="space-y-2">
          <Skeleton className="h-11 w-full" />
          <Skeleton className="h-11 w-full" />
          <Skeleton className="h-11 w-3/4" />
        </div>
      ) : damages.length === 0 ? (
        <p
          className="text-xs text-text-secondary"
          data-testid="warehouse-widget-recent-damages-empty"
        >
          {t("warehouse_dashboard.recent_damages_empty")}
        </p>
      ) : (
        <ul className="space-y-2">
          {damages.map((d) => (
            <li key={d.id}>
              <button
                type="button"
                onClick={() =>
                  onNavigate(d.order_id ? `/orders/${d.order_id}` : "/damages")
                }
                data-testid={`warehouse-recent-damage-${d.id}`}
                className="flex w-full items-center gap-3 rounded-md border border-border-subtle bg-offwhite/40 p-2 text-left transition-colors hover:bg-offwhite focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-champagne"
              >
                <span className="font-mono text-xs text-text-primary">
                  {d.damage_number}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-text-primary">
                    {d.product_name || "—"}
                  </div>
                  <div className="text-[11px] text-text-secondary">
                    {d.reported_at
                      ? formatDateTime(d.reported_at, i18n.language)
                      : ""}
                  </div>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </CardShell>
  );
}

export function WarehouseDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [state, setState] = useState({
    loading: true,
    counts: {},
    damages: [],
  });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      // One request per bucket → separate `status=` filter. Cheap
      // (count-only pages) and lets us click through to the same filter.
      const bucketFetches = BUCKETS.map((b) =>
        ordersService
          .listAll({ status: b.statuses.join(","), page_size: 1, page: 1 })
          .then((r) => [b.key, r.total || 0])
          .catch(() => [b.key, 0])
      );
      const damagesFetch = damagesService
        .list({ page_size: 5, sort: "reported_at:desc" })
        .then((r) => r.items || [])
        .catch(() => []);
      const [bucketEntries, damages] = await Promise.all([
        Promise.all(bucketFetches),
        damagesFetch,
      ]);
      const counts = Object.fromEntries(bucketEntries);
      setState({ loading: false, counts, damages });
    } catch {
      setState({ loading: false, counts: {}, damages: [] });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-6" data-testid="warehouse-dashboard">
      <section
        className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
        data-testid="warehouse-dashboard-buckets"
      >
        {BUCKETS.map((b) => (
          <CountCard
            key={b.key}
            bucket={b}
            count={state.counts[b.key] ?? 0}
            loading={state.loading}
            onClick={() =>
              navigate(
                `/orders?status=${encodeURIComponent(b.statuses.join(","))}`
              )
            }
          />
        ))}
      </section>

      <section
        className="grid gap-4 grid-cols-1 lg:grid-cols-2"
        data-testid="warehouse-dashboard-lower"
      >
        <RecentDamagesWidget
          damages={state.damages}
          loading={state.loading}
          onNavigate={navigate}
        />
        <div className="flex flex-col rounded-lg bg-surface p-5 shadow-card min-h-[220px]">
          <h2 className="text-sm font-semibold text-text-primary">
            {t("warehouse_dashboard.shortcuts_heading")}
          </h2>
          <p className="mt-1 text-xs text-text-secondary">
            {t("warehouse_dashboard.shortcuts_body")}
          </p>
          <div className="mt-4 flex flex-col gap-2">
            <button
              type="button"
              onClick={() => navigate("/delivery")}
              data-testid="warehouse-shortcut-delivery"
              className="flex items-center justify-between rounded-md border border-border-subtle bg-offwhite/40 px-3 py-2 text-sm text-text-primary transition-colors hover:bg-offwhite"
            >
              <span>{t("admin.nav.delivery")}</span>
              <Truck className="h-4 w-4 text-taupe" />
            </button>
            <button
              type="button"
              onClick={() => navigate("/collection")}
              data-testid="warehouse-shortcut-collection"
              className="flex items-center justify-between rounded-md border border-border-subtle bg-offwhite/40 px-3 py-2 text-sm text-text-primary transition-colors hover:bg-offwhite"
            >
              <span>{t("admin.nav.collection")}</span>
              <PackageOpen className="h-4 w-4 text-taupe" />
            </button>
            <button
              type="button"
              onClick={() => navigate("/damages")}
              data-testid="warehouse-shortcut-damages"
              className="flex items-center justify-between rounded-md border border-border-subtle bg-offwhite/40 px-3 py-2 text-sm text-text-primary transition-colors hover:bg-offwhite"
            >
              <span>{t("admin.nav.damages")}</span>
              <AlertTriangle className="h-4 w-4 text-taupe" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
